import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider } from '@mui/material';
import './App.css';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import AuthClassicLayout from 'src/layouts/auth/classic';
import { OverlayWithButton } from './components/OverlayWithButton';
import {
  Dashboard,
  Homework,
  BanBook,
  Login,
  Profile,
  Logout,
  Reports,
  AnxietySurvey,
  SignUp,
  Stripe,
} from './views';
import { NavBar } from './components/NavBar';
import { useSubscriptionStore } from './data';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { theme } from './theme';
import { useApiRepo, Endpoints } from './utils/Api';
import { ConfirmInvite } from './views/ConfirmInvite';
import { ProviderInfo, useProviderInfo } from './utils/ProviderInfoContext';
import { AppConfig } from './utils/AppConfig';
import { useAuthContext } from './auth/hooks';
import { Register } from './pages/Register';
import { VerifyEmail } from './views/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';

const EXCLUDE_OVERLAY_PATHS = [
  'auth/sign-up',
  'auth/login',
  'auth/verify',
  'auth/register',
  'auth/forgot-password',
  '/checkout',
  '/confirm-invite'
];

if (['production', 'development'].includes(AppConfig.environment)) {
  Sentry.init({
    dsn: 'https://830ede8841b2f3f22db55acfd1c18d0c@o4504101307416576.ingest.us.sentry.io/4507647195873280',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    environment: AppConfig.environment,
    // Performance Monitoring
    tracesSampleRate:
      (AppConfig.environment === 'development' && 1.0) ||
      (AppConfig.environment === 'proudction' && 0.05) ||
      1,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      /^https:\/\/api\.ocdfeat\.com/,
      /^https:\/\/betaapi\.ocdfeat\.com/
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

function App() {
  const { isAuthenticated, user } = useAuthContext();
  const { setProviderInfo } = useProviderInfo();
  const { setStatus } = useSubscriptionStore();
  const [triggerSubscriptionCheck, setTriggerSubscriptionCheck] =
    useState(false);
  const [subscriptionCheckLoading, setSubscriptionCheckLoading] =
    useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { getObjectsFromAPI } = useApiRepo();

  const checkSubscriptionStatus = () =>
    setTriggerSubscriptionCheck((value) => !value);

  const [cookies, setCookie, removeCookie] = useCookies(['inviteId']);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const inviteId = searchParams.get('inviteId');

    if (inviteId) {
      setCookie('inviteId', inviteId, { path: '/' });
    }
  }, [location, setCookie]);

  useEffect(() => {
    const fetchSubscription = async () => {
      const api = await getObjectsFromAPI();
      const { data } = await api.get(Endpoints.checkSubscriptionStatus);
      if (data && data.subscription_status) {
        setStatus(data.subscription_status);
      } else {
        setStatus('invalid');
        navigate('/checkout');
      }
      setSubscriptionCheckLoading(false);
    };

    const fetchProviderInfo = async (): Promise<ProviderInfo | null> => {
      const api = await getObjectsFromAPI();

      try {
        const { data } = await api.get(Endpoints.getPatientProviderInfoByUser);

        // delete inviteId cookie if any
        removeCookie('inviteId', { path: '/' });

        return data.data;
      } catch (err) {
        if (axios.isAxiosError(err) && err?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    };

    if (user && user.firebaseUser.emailVerified) {
      fetchProviderInfo().then((providerInfo) => {
        if (providerInfo) {
          // we have a subscription through provider
          setProviderInfo(providerInfo);
          setStatus('valid');
          setSubscriptionCheckLoading(false);
        } else if (cookies.inviteId) {
          // see if we have an inviteId cookie, if so send to invite
          navigate('/confirm-invite');
          setSubscriptionCheckLoading(false);
        } else {
          // no provider, get user subscription
          fetchSubscription();
        }
      });

      // see if user has inviteId, if so there may be a patient record
    } 
  }, [user, triggerSubscriptionCheck, cookies]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider hideIconVariant>
          {location.pathname !== '/sign-up' && <NavBar />}
          {isAuthenticated &&
            !subscriptionCheckLoading &&
            !EXCLUDE_OVERLAY_PATHS.includes(location.pathname) && (
              <OverlayWithButton />
            )}
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute
                  children={<Dashboard/>}
                />
              }
             />
            <Route
              path="/checkout"
              element={<Stripe checkSubscription={checkSubscriptionStatus} />}
            />
            <Route path="auth/login" element={<AuthClassicLayout title='Welcome back'><Login /></AuthClassicLayout>} />
            <Route path="auth/logout" element={<Logout />} />
            <Route path="auth/register" element={<AuthClassicLayout title='Welcome to OCDfeat'><Register /></AuthClassicLayout>} />
            <Route path="auth/forgot-password" element={<AuthClassicLayout title='Welcome back'><ForgotPassword /></AuthClassicLayout>} />
            <Route path="sign-up" element={<ProtectedRoute
                  children={<SignUp/>}
                />} />
            <Route
              path="auth/verify"
              element={
                <ProtectedRoute
                  children={<VerifyEmail/>}
                />
              }
            />
            <Route
              path="/confirm-invite"
              element={
                <ProtectedRoute
                  children={<ConfirmInvite/>}
                />
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute
                  children={<Dashboard/>}
                />
              }
            />
            <Route
              path="/homework"
              element={
                <ProtectedRoute
                  children={<Homework/>}
                />
              }
            />
            <Route
              path="/ban-book"
              element={
                <ProtectedRoute
                  children={<BanBook/>}
                />
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute
                  children={<Reports/>}
                />
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute
                  children={<Profile/>}
                />
              }
            />
            <Route
              path="/anxiety-survey"
              element={
                <ProtectedRoute
                  children={<AnxietySurvey/>}
                />
              }
            />
          </Routes>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
